import { useCallback } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { AugmentedUpdateDocumentInput, DocumentType } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import { GetAssociatedDocumentsDocument } from '../graphql/GetAssociatedDocuments.generated';
import { useUpdateDocumentVersionsMutation } from '../graphql/UpdateDocumentVersions.generated';

interface AmendmentDocument {
  id: string;
  name: string;
  type: DocumentType;
  isCurrentDocument: boolean;
}

export function buildDocumentVersionInputs(
  originalDocuments: AmendmentDocument[],
  reorderedDocuments: AmendmentDocument[],
  currentDocumentId: string
) {
  const currentDocumentCount = reorderedDocuments.filter(
    (document) => document.id === currentDocumentId
  ).length;
  if (currentDocumentCount !== 1) {
    throw new Error(
      'The document chain must contain the current document exactly once'
    );
  }

  const reorderedDocumentIds = reorderedDocuments.map(
    (document) => document.id
  );
  if (new Set(reorderedDocumentIds).size !== reorderedDocumentIds.length) {
    throw new Error('The document chain must contain unique document IDs');
  }

  const documentIdsToClear = new Set([
    ...originalDocuments.map((document) => document.id),
    ...reorderedDocumentIds,
  ]);
  const clearInputs: AugmentedUpdateDocumentInput[] = Array.from(
    documentIdsToClear,
    (id) => ({
      id,
      update: {
        clearNextVersion: true,
        clearPreviousVersion: true,
      },
    })
  );
  const chainUpdateInputs: AugmentedUpdateDocumentInput[] =
    reorderedDocuments.map((document, index) => {
      const previousDocument = index > 0 ? reorderedDocuments[index - 1] : null;

      return {
        id: document.id,
        update: {
          ...(previousDocument && { nextVersionID: previousDocument.id }),
        },
      };
    });
  const currentDocumentIndex = reorderedDocuments.findIndex(
    (document) => document.id === currentDocumentId
  );
  // The backend uses the first input as the owner of the combined summary.
  // Keep the current page first without changing the saved chain links.
  const updateInputs = [...chainUpdateInputs];
  if (currentDocumentIndex > 0) {
    const [currentDocumentInput] = updateInputs.splice(currentDocumentIndex, 1);
    if (currentDocumentInput) {
      updateInputs.unshift(currentDocumentInput);
    }
  }

  return { clearInputs, updateInputs };
}

export function useManageDocumentAmendments(documentId: string) {
  const [updateDocumentVersions, { loading, error }] =
    useUpdateDocumentVersionsMutation();

  useReportError('error updating document versions', error);

  const handleUpdateDocumentVersions = useCallback(
    async (
      originalDocuments: AmendmentDocument[],
      reorderedDocuments: AmendmentDocument[]
    ) => {
      try {
        // Clear the old and new members first so removed documents do not keep
        // stale links to the saved chain.
        const { clearInputs, updateInputs } = buildDocumentVersionInputs(
          originalDocuments,
          reorderedDocuments,
          documentId
        );
        const affectedDocumentIds = clearInputs.map((input) => input.id);

        diagnostics.debug('Updating document versions', { updateInputs });

        const result = await updateDocumentVersions({
          variables: {
            setDocumentVersionsInput: updateInputs,
            clearDocumentVersionsInput: clearInputs,
          },
          update: (cache) => {
            // Each document page caches this query separately. Evict every old
            // and new member so later navigation cannot restore a stale chain.
            for (const affectedDocumentId of affectedDocumentIds) {
              cache.evict({
                id: 'ROOT_QUERY',
                fieldName: 'getAssociatedDocuments',
                args: { documentID: affectedDocumentId },
              });
            }
          },
          refetchQueries: [
            {
              query: GetAssociatedDocumentsDocument,
              variables: { documentID: documentId },
            },
          ],
          awaitRefetchQueries: true,
        });

        diagnostics.debug('Document versions updated successfully', { result });
      } catch (err) {
        diagnostics.error('Failed to update document versions', err as Error);
        throw err;
      }
    },
    [documentId, updateDocumentVersions]
  );

  return {
    updateDocumentVersions: handleUpdateDocumentVersions,
    loading,
    error,
  };
}
